import type { StateObject } from "../../../redux/types"
import type { TransactionsReportItem } from "./types"
import type { TransactionsReportTableDataItem } from "./TransactionsReportTable"
import { getTransactionsReport } from "../selectors"
import type { QueryApiParameters } from "../../../../utils/queryApi/queryApiRequest"
import { getFilters, getSelectedLocationIdsFromGlobalFilter } from "../../../filters/selectors"
import { T } from "../../../../utils/queryApi/expressions"
import { composeSearchAmountExpression, composeSearchExpressions } from "../../../filters/utils"
import { getCurrentPath } from "../../../router/selectors"
import { GLOBAL_FILTER_IDENTIFIER } from "../../../filters/reducer"

const mapReportsToTreeData = (
    state: StateObject,
    reports: TransactionsReportItem[]
): TransactionsReportTableDataItem[] => {
    return reports.map((t: TransactionsReportItem) => {
        return {
            id: t.id,
            timestamp: t.timestamp,
            transactionNumber: t.transactionNumber,
            orderNumber: t.orderNumber,
            transactorName: t.transactorName,
            assigneeName: t.assigneeName,
            locationName: t.locationName,
            terminalName: t.terminalName,
            operation: t.operation,
            entryMode: t.entryMode,
            result: t.result,
            resultMessage: t.resultMessage || null,
            paymentType: t.paymentType,
            paymentSubtype: t.paymentSubtype,
            total: t.total,
            currency: t.currency,
            initiatedOffline: t.initiatedOffline,
            approvalCode: t.approvalCode,
            externalPaymentType: t.externalPaymentType || null,
        }
    })
}

export const getDataForReportsTable = (state: StateObject): TransactionsReportTableDataItem[] => {
    return mapReportsToTreeData(state, getTransactionsReport(state))
}

export const isTransactionsReportAgendaOpened = (state: StateObject): boolean => {
    const currentPath = getCurrentPath(state)

    if (currentPath && currentPath.query && currentPath.query.tab) {
        return currentPath.query.tab === "transactions" || currentPath.query.tab === "orders"
    }

    return false
}

export const getTransactionsQueryApiParam = (state: StateObject): QueryApiParameters => {
    const globalFilters = getFilters(state, GLOBAL_FILTER_IDENTIFIER)
    const filters = getFilters(state, "transactionsReport")
    let filter = globalFilters.timestamp
        ? T("timestamp").ge(globalFilters.timestamp.from).and(T("timestamp").le(globalFilters.timestamp.to))
        : T("timestamp").ge(0)

    if (filters.search && filters.search.value.length > 0 && filters.search.fields.length > 0) {
        const searchFields = filters.search.fields
        const searchValue = filters.search.value
        // Search in defined fields
        filter = filter.and(
            composeSearchExpressions(searchFields, searchValue).or(
                composeSearchAmountExpression("total", searchValue) // Search in amount field
            )
        )
    }

    const selectedLocationIds = getSelectedLocationIdsFromGlobalFilter(state)
    filter = selectedLocationIds.length > 0 ? filter.and(T("location_id").inRange(selectedLocationIds)) : filter
    filter =
        filters.operation && filters.operation.length > 0
            ? filter.and(T("operation").inRange(filters.operation))
            : filter
    filter = filters.result && filters.result.length > 0 ? filter.and(T("result").inRange(filters.result)) : filter
    filter =
        filters.payment_type && filters.payment_type.length > 0
            ? filter.and(T("payment_type").inRange(filters.payment_type))
            : filter
    const offset = (filters.pagination.currentPage - 1) * filters.pagination.pageLimit
    return {
        filter: filter,
        ...(filters.sort
            ? {
                  sort: [filters.sort],
              }
            : null),
        limit: filters.pagination.pageLimit,
        offset: offset,
    }
}
